import { createContext, CSSProperties, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from 'core/context/app.context';
import { SitePlatform } from 'core/enums/site-platform';
import { Util } from 'utils/common.utils';

type BackgroundContext = {
  backgroundImage: string | undefined;
  backgroundStyles: CSSProperties | undefined;
  imageStyles: CSSProperties | undefined;
  resetToDefault: () => void;
  setCustomBackground: (props: {
    backgroundImage?: string | undefined,
    imageStyles?: CSSProperties | undefined,
    backgroundStyles?: CSSProperties | undefined,
  }) => void;
};

export const MainBackgroundContext = createContext<BackgroundContext | null>(null);

export const useMainBackgroundContext = () => {
  const bgContext = useContext(MainBackgroundContext);
  if (!bgContext) {
    throw new Error('MainBackgroundContext has been used without being initialized.');
  }

  return bgContext;
};

export function MainBackgroundProvider({ children }: { children: ReactNode }) {
  const appContext = useContext(AppContext);

  const defaultImage = useMemo(() => getDefaultBgImage(appContext.platform), [appContext.platform]);

  const [isDefaultBackground, setIsDefaultBackground] = useState(true);
  const [backgroundImage, setBackgroundImage] = useState(defaultImage);
  const [backgroundStyles, setBackgroundStyles] = useState<CSSProperties | undefined>(undefined);
  const [imageStyles, setImageStyles] = useState<CSSProperties | undefined>(undefined);

  useEffect(() => {
    if (!isDefaultBackground) return;
    setBackgroundImage(defaultImage);
    setBackgroundStyles(undefined);
    setImageStyles(undefined);
  }, [isDefaultBackground, defaultImage]);

  function resetToDefault() {
    setIsDefaultBackground(true);
  }

  function setCustomBackground(props: {
    backgroundImage?: string | undefined,
    imageStyles?: CSSProperties | undefined,
    backgroundStyles?: CSSProperties | undefined,
  }) {
    setIsDefaultBackground(false);
    'backgroundImage' in props && setBackgroundImage(props.backgroundImage);
    'imageStyles' in props && setImageStyles(props.imageStyles);
    'backgroundStyles' in props && setBackgroundStyles(props.backgroundStyles); 
  }

  return (
    <MainBackgroundContext.Provider
      value={{
        backgroundImage,
        backgroundStyles,
        imageStyles,
        resetToDefault,
        setCustomBackground,
      }}
    >
      {children}
    </MainBackgroundContext.Provider>
  );
}

const getDefaultBgImage = (platform: SitePlatform) =>
  platform === SitePlatform.gotv
    ? undefined // TODO: add gotv default background
    : Util.GetImageAsset(`page-background-desktop.jpg`);